import {
  CreateEmissionInput,
  EmissionType,
  ProjectFragment,
  ProjectVersionFragment,
} from '../../generated/graphql'
import { sortByProperty } from '../../util/array-utility'
import { parseLocaleDecimalToFloat } from '../../util/formatters'
import type { EmissionWithVersionId } from './carbon-footprint-tables/carbon-footprint-table-util'
import type { FormValues } from './EmissionDialog'

export const getDefaultFormValues = (
  project: ProjectFragment,
): Partial<FormValues> | null => {
  const defaultVersion = project.versions.find((version) => version.isDefault)

  if (defaultVersion && defaultVersion.buildings.length > 0) {
    return {
      projectVersionId: defaultVersion.id,
      buildingId: sortByProperty(defaultVersion.buildings, 'name')[0].id,
      emissionType: EmissionType.Initial,
    }
  }
  const versionWithBuildings = project?.versions.find(
    (version) => version.buildings.length > 0,
  )
  if (versionWithBuildings) {
    return {
      projectVersionId: versionWithBuildings.id,
      buildingId: versionWithBuildings.buildings[0].id,
      emissionType: EmissionType.Initial,
    }
  }
  return null
}

const emissionTypesWithLargeEmissionUnit = [
  EmissionType.Disassembly,
  EmissionType.Handprint,
  EmissionType.Initial,
  EmissionType.Maintenance,
]

export const mapFormValuesToEmissionInput = (
  values: FormValues,
): CreateEmissionInput => {
  const { emissionType, value } = values

  const multiplier = emissionTypesWithLargeEmissionUnit.includes(emissionType)
    ? 1000
    : 1

  return {
    emissionType,
    value: parseLocaleDecimalToFloat(value) * multiplier,
  }
}
/**
 *
 * @param emission
 * @param versionId
 * @param buildingId
 * @returns Emissions with large emission unit are presented in tons,
 * so a division is performed before
 */
export const mapEmissionToFormValues = (
  emission: EmissionWithVersionId,
  versionId: string,
  buildingId: string,
): Partial<FormValues> => {
  const divider = emissionTypesWithLargeEmissionUnit.includes(
    emission.emissionType,
  )
    ? 1000
    : 1
  return {
    buildingId,
    projectVersionId: versionId,
    emissionType: emission.emissionType,
    value: String(emission.value / divider),
  }
}

export const validateEmission = (
  versions: ProjectVersionFragment[],
  versionId: string,
  buildingId: string,
  emissionType: EmissionType,
): boolean => {
  const existingEmission = versions
    .find((version) => version.id === versionId)
    ?.buildings.find((building) => building.id === buildingId)
    ?.emissions.find((emission) => emission.emissionType === emissionType)
  return !existingEmission
}

const calculateOneTimeEmissionValue = (
  inherentValue: number,
  areaMultipliedByYears: number,
): number => inherentValue * areaMultipliedByYears

const calculateInherentValueFromOneTimeEmissionValue = (
  value: number,
  areaMultipliedByYears: number,
): number => value / areaMultipliedByYears

const calculateAnnualEmissionValue = (
  inherentValue: number,
  area: number,
): number => inherentValue * area

const calculateInherentValueFromAnnualEmissionValue = (
  value: number,
  area: number,
): number => value / area

export type ValueCalculationInput = {
  emissionType: EmissionType
  area: number
  inspectionLengthInYears: number
  inherentValue: number
}
export const calculateValueByEmissionType = ({
  emissionType,
  area,
  inspectionLengthInYears,
  inherentValue,
}: ValueCalculationInput): number =>
  emissionType === EmissionType.Recurring
    ? calculateAnnualEmissionValue(inherentValue, area)
    : calculateOneTimeEmissionValue(
        inherentValue,
        area * inspectionLengthInYears,
      )

export type InherentValueCalculationInput = {
  emissionType: EmissionType
  area: number
  inspectionLengthInYears: number
  value: number
}

export const calculateInherentValueByEmissionType = ({
  emissionType,
  area,
  inspectionLengthInYears,
  value,
}: InherentValueCalculationInput): number =>
  emissionType === EmissionType.Recurring
    ? calculateInherentValueFromAnnualEmissionValue(value, area)
    : calculateInherentValueFromOneTimeEmissionValue(
        value,
        area * inspectionLengthInYears,
      )

export const isLargeUnitType = (emissionType: EmissionType): boolean =>
  emissionTypesWithLargeEmissionUnit.includes(emissionType)
